import type { ActiveModel } from '@alt-point/active-models/src'
import { merge } from 'lodash-es'
import {
  defineComponent,
  h,
  ref,
  toValue,
  type Component,
  type VNodeProps,
  type AllowedComponentProps,
  Transition,
  type VNode,
} from 'vue'

import { UModal, USlideover } from '#components'
import type { ModalProps } from '#ui/types'

export type ComponentAtts<C extends Component> = C extends new (
  ...args: any
) => any
  ? Omit<
    InstanceType<C>['$props'],
      keyof VNodeProps | keyof AllowedComponentProps
  >
  : never

type ExternalComponentAtts<C extends Component> = Omit<
  ComponentAtts<C>,
  'formModel' | 'onSubmit' | 'onCancel'
>

export type PropertiesMaybeRef<ObjectType extends object> = {
  [key in keyof ObjectType]: MaybeRef<ObjectType[key]>
}

export type OverlayType = 'modal' | 'slideover'
const overlayTypeMapComponent: { [key in OverlayType]: Component } = {
  modal: UModal,
  slideover: USlideover,
}

type OverlaySlots = Record<any, null | Component | (() => VNode)>
export type UseFormModalConfig<
  ReadModelType,
  WriteModelType = ReadModelType,
  FormComponent extends Component = Component,
> = {
  overlayType?: OverlayType
  loadModel?: (id: string | number) => Promise<ReadModelType>
  writeModel?: (model: ReadModelType | undefined) => WriteModelType
  formComponent:
    | MaybeRef<FormComponent>
    | ((model: ReadModelType | undefined) => FormComponent)
  formAttrs?: PropertiesMaybeRef<ExternalComponentAtts<FormComponent>>
  factory?: (payload?: ReadModelType) => ReadModelType | undefined
  onSubmit?: (...args: any) => Promise<void> | boolean | Promise<boolean>
  onClose?: () => void
  closeAfterSubmit?: boolean
  dictionaries?: MaybeRefOrGetter<{
    title: string | ((model: ReadModelType | undefined) => string)
  }>
  modalProps?: ModalProps
  dialogEmits?: {
    [key: string]: (...args: any) => void
  }
  overlaySlots?: OverlaySlots
}

export const useFormModal = <
  ReadModelType extends ActiveModel | unknown = ActiveModel,
  WriteModelType extends ActiveModel | unknown = ReadModelType,
  FormComponent extends Component = Component,
>(
  config: MaybeRefOrGetter<
    UseFormModalConfig<ReadModelType, WriteModelType, FormComponent>
  >,
) => {
  const staticConfig = toValue(config)
  const overlay = useOverlay()
  const modalInstance = ref()

  const fullModel = ref<ReadModelType | undefined>(
    staticConfig.factory?.() || undefined,
  )
  const model = ref<WriteModelType | undefined>(undefined)
  const prevModel = ref<WriteModelType | undefined>(undefined)

  // Создаем reactive wrapper компонент для отслеживания изменений formAttrs
  const FormWrapper = defineComponent({
    name: 'FormWrapper',
    setup() {
      return () => {
        const formAttrs
          = toValue(config).formAttrs || ({} as Record<string, any>)

        const reactiveAttrs: Record<string, any> = {}
        for (const key in formAttrs) {
          reactiveAttrs[key] = toValue(formAttrs[key])
        }

        const rawConfig = toValue(config)
        const formComponentSetting = rawConfig.formComponent
        const formComponent
          = typeof formComponentSetting === 'function'
            ? formComponentSetting(fullModel.value)
            : unref(formComponentSetting)

        return h(formComponent, {
          readModel: fullModel.value,
          prevModel: prevModel.value,
          writeModel: model.value,
          onSubmit: async (...args: any) => {
            const result = await staticConfig.onSubmit?.(...args)

            if (result !== false) {
              staticConfig.closeAfterSubmit !== false && closeModal()
            }
          },
          onCancel: () => {
            closeModal()
          },
          ...reactiveAttrs,
        })
      }
    },
  })

  function startAdd(value?: ReadModelType) {
    if (value) {
      fullModel.value = staticConfig.factory?.(value) || undefined
    }
    model.value = toValue(config).writeModel?.(value) ?? fullModel.value

    openModal()
  }

  async function startEdit(value: ReadModelType | string | number) {
    const modelIsId = typeof value === 'string' || typeof value === 'number'
    fullModel.value = modelIsId
      ? (await staticConfig.loadModel?.(value)) || undefined
      : staticConfig.factory?.(value) || undefined

    model.value
      = toValue(config).writeModel?.(fullModel.value) ?? fullModel.value

    if (modelIsId && fullModel.value && staticConfig.factory) {
      prevModel.value
        = toValue(config).writeModel?.(fullModel.value) ?? fullModel.value
    }

    openModal()
  }

  function openModal() {
    const title = toValue(toValue(config).dictionaries)?.title
    const titleKey
      = typeof title === 'function' ? title(fullModel.value) : title
    const modalTitle = titleKey || undefined
    const rawConfig = toValue(config)

    const slots = Object.assign({}, rawConfig.overlaySlots, {
      body: () =>
        h(Transition, { duration: 300, name: 'fade' }, () => h(FormWrapper)),
    })

    modalInstance.value = overlay.create(
      h(
        overlayTypeMapComponent[rawConfig.overlayType || 'modal'],
        merge(
          {
            title: modalTitle,
            'onAfter:leave': () => {
              fullModel.value = undefined
              modalInstance.value = null
              staticConfig.onClose?.()
            },
            dismissible: false,
            ui: {
              body: 'flex flex-col',
            },
          },
          staticConfig.modalProps,
        ),
        slots,
      ),
    )

    modalInstance.value?.open()
  }

  function closeModal() {
    modalInstance.value?.close()
  }

  return {
    startEdit,
    startAdd,
    openModal,
    closeModal,

    fullModel,
    model,
    prevModel,
  }
}
